import { Component, ViewChild, Input, Output, EventEmitter} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { CrudService } from 'src/app/services/crud.service';
import { DxButtonModule, DxDataGridComponent, DxDataGridModule, DxLoadPanelModule, DxSwitchModule } from 'devextreme-angular';
import { environment } from 'src/environments/environment';
import { PromotionHeader } from '../models/promotion-header.model';
import { PromotionGroup } from '../models/promotion-group.model';
import { PromotionCategory } from '../models/promotion-category.model';
import { NotifyService } from '../services/notify.service';
import { Observable, map, firstValueFrom } from 'rxjs';
import { DuplicateResolutionComponent } from '../settings/duplicate-resolution/duplicate-resolution.component';

@Component({
    selector: 'app-promotion-group-selection',
    templateUrl: './promotion-group-selection.component.html',
    styleUrls: ['./promotion-group-selection.component.css'],
    imports: [ AsyncPipe, DxDataGridModule, DxSwitchModule, DxButtonModule, DxLoadPanelModule, DuplicateResolutionComponent ],
    standalone: true
})
export class PromotionGroupSelectionComponent {
  @ViewChild('promotionGroupSelectionGrid', {static: false}) dataGrid: DxDataGridComponent;
  @Output() loadParentData: EventEmitter<any> = new EventEmitter();
  @Output() closeChild: EventEmitter<any> = new EventEmitter();
  @Input() parent: string;
  header: PromotionHeader;
  promotionGroups$: Observable<PromotionGroup[]>;
  promotionCategories$: Observable<PromotionCategory[]>;
  slotted: boolean = false;
  dataLoading: boolean = true;
  gridMode: string = 'multiple';
  duplicateData: any[] = [];
  showDuplicateResolution: boolean = false;
  loading: boolean = false;
  saveButtonDisabled: boolean = false;
  private apiUrl: string = environment.baseApiUrl;
  private pendingSaveData: any = null;

  constructor(private crudService: CrudService,
              private notifyService: NotifyService) {
    if(localStorage.getItem('selectedPromo') !== null) {
      this.header = JSON.parse(localStorage.getItem('selectedPromo') || '{}');
    }
    
    this.promotionCategories$ = this.crudService.newGetData(`${this.apiUrl}planning-categories`);
    this.loadPromotionGroups();
  }

  loadPromotionGroups() {
    this.promotionGroups$ = this.crudService.newGetData(`${this.apiUrl}promotion-groups-not-on-promotion?promotion_header_id=${this.header.id}`).pipe(
      map((result: any) => {
        this.dataLoading = false;
        return result;
      })
    );
  }

  async selectPromotionGroups(e: any) {
    let userDetails: any = {};
    this.dataLoading = true;
    if(localStorage.getItem('userDetails') !== null) {
      userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    }
    const promotionGroups: PromotionGroup[] = this.dataGrid.instance.getSelectedRowsData();
    if(!this.slotted) {
      promotionGroups.forEach((group: PromotionGroup) => {
        group.slot_type = '';
      });
    }
    const data: any ={
      promotion_header_id: this.header.id,
      promotion_groups: promotionGroups
    }
    
    // Prepare the save data first
    const saveData: any = {
      location_id: userDetails.data.location_id,
      promotion_header_id: this.header.id,
      buying_group_id: this.header.buying_group_id,
      promotion_groups: promotionGroups
    };

    try {
      const duplicateCheckResult: any = await firstValueFrom(
        this.crudService.insertData(`${this.apiUrl}check-for-matching-items`, data)
      );
      
      // Check if duplicates found (data array has items)
      if (duplicateCheckResult.data && duplicateCheckResult.data.length > 0) {
        // Store the save data for later use
        this.pendingSaveData = saveData;
        // Show duplicate resolution popup
        this.duplicateData = duplicateCheckResult.data;
        this.showDuplicateResolution = true;
        this.dataLoading = false;
        return;
      }
      
      // No duplicates found, proceed with save immediately
      await this.proceedWithSave(saveData);
      
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      this.notifyService.tellUser({ 
        success: false, 
        message: 'Error checking for duplicate items' 
      });
      this.dataLoading = false;
    }
  }

  async onDuplicateResolutionSelected(resolutionResult: any): Promise<void> {
    this.showDuplicateResolution = false;
    
    if (resolutionResult.success) {
      console.log('Duplicates resolved successfully');
      // Continue with the original save process using stored data
      if (this.pendingSaveData) {
        await this.proceedWithSave(this.pendingSaveData);
        this.pendingSaveData = null; // Clear the stored data
      } else {
        console.error('No pending save data found');
        this.notifyService.tellUser({ 
          success: false, 
          message: 'Error: No pending save data found' 
        });
      }
    } else {
      console.error('Error resolving duplicates:', resolutionResult);
      this.notifyService.tellUser({ 
        success: false, 
        message: resolutionResult.failed ? 
          `Some operations failed: ${resolutionResult.failed.join(', ')}` :
          'Error resolving duplicates'
      });
    }
    this.dataLoading = false;
  }

  onDuplicateResolutionCancelled(): void {
    this.showDuplicateResolution = false;
    this.dataLoading = false;
    this.pendingSaveData = null; // Clear the stored data
  }

  // Update proceedWithSave to handle the Promise properly
  async proceedWithSave(data: any): Promise<void> {
    try {
      const result: any = await firstValueFrom(
        this.crudService.insertData(`${this.apiUrl}insert-promotion-details`, data)
      );
      
      this.notifyService.tellUser(result);
      console.log('Inserted Promotion Details: ', result);
      this.loadParentData.emit();
      this.closeChild.emit(result);
      this.dataLoading = false;
      
    } catch (error) {
      console.error('Error saving promotion details:', error);
      this.notifyService.tellUser({ 
        success: false, 
        message: 'Error saving promotion details' 
      });
      this.dataLoading = false;
    }
  }

  slottedPromotionGroups(e: any) {
    this.dataLoading = true;
    if(e.value) {
      this.promotionGroups$ = this.crudService.newGetData(`${this.apiUrl}selected-promotion-groups?`+
        `promotion_period_id=${this.header.promotion_period_id}&`+
        `promotion_start=${this.header.promotion_start}&`+
        `promotion_end=${this.header.promotion_end}&`+
        `buying_group_id=${this.header.buying_group_id}`).pipe(
        map((result: any) => {
          this.slotted = true;
          this.dataLoading = false;
          return result.data;
        })
      );
    } else {
      this.dataLoading = true;
      this.slotted = false;
      this.loadPromotionGroups();
    }
  }

  refreshPromotionGroups() {
    this.dataLoading = true;
    this.loadPromotionGroups();
  }

  onHiding(event: any) {
    this.dataGrid.instance.clearSelection();
    this.dataGrid.instance.clearFilter();
  }

}
